import {
  Button as DesignSystemButton,
  LockScreen,
  Shield,
} from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { useLockScreenTexts } from "../../../hooks/useLockScreenTexts";
import { usePinErrorHandler } from "../../../hooks/usePinErrorHandler";
import { pinApi } from "../../../services/pin.api";
import {
  pinAccepted,
  selectHasPin,
} from "../../../store/reducers/sessionSlice";

/**
 * MED-1622: change the PIN from the settings page. Shown only once the user
 * has a PIN (and never against an API without the PIN feature).
 */
export const ChangePinSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handlePinError = usePinErrorHandler();
  const hasPin = useSelector(selectHasPin);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const texts = useLockScreenTexts({ cancel: t("pinLock.cancel") });

  if (hasPin !== true) return null;

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const changePin = async (currentPin: string, newPin: string) => {
    try {
      await pinApi.changePin(currentPin, newPin);
      dispatch(pinAccepted());
      close();
      toast.success(t("pinLock.change.success"));
    } catch (e) {
      setError(handlePinError(e));
      throw e;
    }
  };

  return (
    <section className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200">
      <div className="flex items-center space-x-2">
        <Shield className="w-4 h-4 text-neutral-500" />
        <h2 className="text-lg font-semibold text-neutral-900">
          {t("pinLock.change.title")}
        </h2>
      </div>
      <p className="text-xs text-neutral-500">{t("pinLock.change.hint")}</p>
      <DesignSystemButton type="button" onClick={() => setOpen(true)}>
        {t("pinLock.change.button")}
      </DesignSystemButton>
      {open && (
        <LockScreen
          mode="change"
          texts={texts}
          error={error}
          onChangePin={changePin}
          onCancel={close}
        />
      )}
    </section>
  );
};
